/* eslint-disable angular/timeout-service */

import { dialogComponentModule } from '../../dialog/dialog';
import { warnBeforeClosing } from '../../../utils/dom-utils';

const DEFAULT_MESSAGES = {
  unloadMessage:
    'You have unsaved changes that will be lost if you leave this page. Are you sure you want to leave this page?',
  routeChangeMessage:
    '<p>You have unsaved changes that will be lost if you leave this page.</p><p>Are you sure you want to leave this page?</p>',
  routeChangeWithSaveMessage:
    '<p>You have unsaved changes.</p><p>Would you like to save them before continuing?</p>',
};

let isDialogVisible = false;

export const warnOnChangesComponentModule = angular
  .module('Q.components.forms.warn-on-changes', [dialogComponentModule])

  /**
   * @description
   * Allows a generic form to alert the user if they are going to lose changes
   * The warnOnChanges attribute can take an optional hash of these properties:
   *
   * - handleSave A function that will be called if the user clicks "Save" on the Save/Discard/Cancel dialog.
   * - handleDiscard A function that will be called if the user clicks "Discard" on the Save/Discard/Cancel dialog.
   * - handleFinish A function that will be called if the user leaves the view, either by clicking Discard or if the
   *       form did not have any changes, or after the user successfully saves
   * - unloadMessage Override default message to show if the page changes
   * - routeChangeMessage Override message to show if the route changes
   * - routeChangeWithSaveMessage Override message to show if the route changes and you provide an handleSave method
   *
   */
  .directive('warnOnChanges', function (warnOnChangesService) {
    return {
      restrict: 'A',
      require: 'form',
      link(scope, elem, attrs, ngForm) {
        const options = scope.$eval(attrs.warnOnChanges) || {};

        warnOnChangesService.enable(
          angular.extend(options, {
            hasChanges() {
              return ngForm.$dirty;
            },
          }),
        );

        scope.$on('$destroy', warnOnChangesService.disable);
      },
    };
  })

  .factory(
    'warnOnChangesService',
    function ($window, $rootScope, $state, $q, dialogService) {
      let _options;
      let _stateListener;
      let _logoutListener;
      let _warnBeforeClosingListener;

      return {
        enable,
        disable,
      };

      /**
       * Parameters are provided as an object
       * @param options Options
       * @param options.hasChanges Required: a method that determines if there are any changes
       * @param options.ignoreRouteChange: optional method that should return true if the route change is ignored
       * @param options.handleSave A function that will be called if the user clicks "Save" on the Save/Discard/Cancel dialog.
       * @param options.handleDiscard A function that will be called if the user clicks "Discard" on the Save/Discard/Cancel dialog.
       * @param options.handleFinish A function that will be called if the user leaves the view, either by clicking Discard or if the
       *       form did not have any changes
       * @param options.unloadMessage Override default message to show if the page changes
       * @param options.routeChangeMessage Override message to show if the route changes
       * @param options.routeChangeWithSaveMessage Override message to show if the route changes and you provide an handleSave method
       * @returns {{}}
       */
      function enable(options) {
        _options = angular.extend(
          {
            ignoreRouteChange: angular.noop,
            handleDiscard: angular.noop,
            handleFinish: angular.noop,
          },
          DEFAULT_MESSAGES,
          options || {},
        );

        if (!_options.hasChanges) {
          throw new Error(
            'warnOnChangesService: Missing required option hasChanges!',
          );
        }

        // listen to whole page changes
        _warnBeforeClosingListener = warnBeforeClosing(_onBeforeUnload);
        // listen to state changes
        _stateListener = $rootScope.$on(
          '$stateChangeStart',
          _onBeforeStateChange,
        );
        // listen to logout
        _logoutListener = $rootScope.$on('app.beforeLogout', _onBeforeLogout);
      }

      function disable() {
        if (_warnBeforeClosingListener) _warnBeforeClosingListener();
        if (_stateListener) _stateListener();
        if (_logoutListener) _logoutListener();
        _stateListener = null;
        _logoutListener = null;
        $window.setTimeout(() => (_options = null));
      }

      //// private functions

      /**
       * Used to handle the window onBeforeUnload event.
       * @returns {*} Message if there are changes
       * @private
       */
      function _onBeforeUnload(evt) {
        if (isDialogVisible || !_options) return undefined;

        if (_options.hasChanges(evt)) {
          if (_options.warnOnUnload === 'auto') {
            if (_options.handleSave) {
              _options.handleSave();
            }
            _options.handleFinish();
          }
          // we can't clean up if they close the page even after the warning
          return angular.isFunction(_options.unloadMessage)
            ? _options.unloadMessage()
            : _options.unloadMessage;
        } else {
          _options.handleFinish();
        }
      }

      /**
       * Handles state change events.  If there is a change, AND we are not navigating to the same
       * scope (as in, scopedKey scope, not angular scope), we stop the navigation and throw up a
       * dialog.
       *
       * If the developer provided a save callback, we can use a dialog that provides three options:
       *     - Save: save and go
       *     - Discard: forget changes and go
       *     - Cancel: don't go
       *
       * @param evt State change event
       * @param toState State we are transitioning to
       * @param toParams StateParams we are transitioning to
       * @param fromState State we are transitioning from
       * @param fromParams StateParams we are transitioning from
       * @param options State change options
       * @returns {Promise}
       * @private
       */
      function _onBeforeStateChange(
        evt,
        toState,
        toParams,
        fromState,
        fromParams,
        options,
      ) {
        if (!_stateListener || !_options) return $q.reject();

        if (toState && toState.name === 'error') return $q.reject();

        if (
          _options.ignoreRouteChange(
            evt,
            toState,
            toParams,
            fromState,
            fromParams,
            options,
          )
        )
          return $q.reject();

        return _onBeforeEvent(evt).then(function () {
          if (_options) {
            // async check!
            _options.handleFinish();
          }
          disable();
          $state.transitionTo(toState.name, toParams, options);
        });
      }

      /**
       * Handles logout events.  If there is a change we stop the navigation and throw up a dialog.
       *
       * If the developer provided a save callback, we can use a dialog that provides three options:
       *     - Save: save and go
       *     - Discard: forget changes and go
       *     - Cancel: don't go
       *
       * @param evt State change event
       * @returns {Promise}
       * @private
       */
      function _onBeforeLogout(evt) {
        if (evt.defaultPrevented || !_logoutListener) return $q.reject();

        return _onBeforeEvent(evt, true).then(function () {
          if (_options) {
            _options.handleFinish();
          }
          disable();
        });
      }

      /**
       * Handles an event broadcast. This method does the bulk of the work handling an event broadcast.
       * It returns a promise that is resolved if the event was canceled and the event should continue,
       * or rejected if the event was canceled and should remain canceled.
       *
       * @param evt
       * @param preventDefaultOnAuto
       * @returns {Promise}
       * @private
       */
      function _onBeforeEvent(evt, preventDefaultOnAuto = false) {
        const _reject = $q.reject();
        let cleanup = () => {
          // clean up after ourselves if there are no changes, and we are leaving the page
          _options.handleFinish();
          disable();
          return _reject;
        };

        if (evt.defaultPrevented || !_options) return _reject;

        if (_options.warnOnUnload === 'auto') {
          if (_options.hasChanges(evt) && _options.handleSave) {
            let ret = _options.handleSave();
            if (preventDefaultOnAuto) {
              evt.preventDefault();
              evt.promise = ret;
            }
            return ret;
          } else {
            return cleanup();
          }
        }

        // allows stop-route-change-if-invalid directive to cancel this functionality
        if (
          $rootScope.$broadcast('warnOnChanges.shouldContinue').defaultPrevented
        )
          return _reject;

        if (_options.hasChanges(evt)) {
          isDialogVisible = true;
          evt.preventDefault();
          let msg = _options.handleSave
            ? _options.routeChangeWithSaveMessage
            : _options.routeChangeMessage;
          msg = angular.isFunction(msg) ? msg() : msg;
          if (_options.handleSave) {
            evt.promise = dialogService.save(msg).then(function (result) {
              if (_options) {
                if (result.id === 'save') {
                  const ret = _options.handleSave();
                  if (ret && ret.then) {
                    return ret;
                  }
                } else if (result.id === 'discard') {
                  _options.handleDiscard();
                }
              }
              return $q.when();
            });
          } else {
            evt.promise = dialogService.confirm(msg).then(function (result) {
              return result.id === 'yes' ? $q.when() : _reject;
            });
          }
          evt.promise.finally(() => (isDialogVisible = false));
          return evt.promise;
        } else {
          return cleanup();
        }
      }
    },
  );
